import { DayDate } from '@deltager/organizers/admin/core/date-format/dayDate';
import dayjs from 'dayjs';

export type FormatType = 'None' | 'Local' | 'Utc';

export class Timespan {
  constructor(
    public hours: number,
    public minutes: number,
  ) {}

  get isValid(): boolean {
    const isFullDay = this.hours === 24 && this.minutes === 0;

    return isFullDay || (this.hours >= 0 && this.hours <= 23 && this.minutes >= 0 && this.minutes <= 59);
  }

  get totalMinutes(): number {
    return this.hours * 60 + this.minutes;
  }

  static equals(timeFst: Timespan | null, timeSnd: Timespan | null): boolean {
    return timeFst.hours === timeSnd.hours && timeFst.minutes === timeSnd.minutes;
  }

  static rangeValid(start: Timespan | null, end: Timespan | null): boolean {
    if (!start || !end || !start.isValid || !end.isValid) {
      return false;
    }

    if (start.hours > end.hours) {
      return false;
    }

    if (end.hours > start.hours) {
      return true;
    }

    return end.hours === start.hours && end.minutes > start.minutes;
  }

  static fromMinutes(minutes: number | null): Timespan | null {
    if (minutes === null) {
      return null;
    }

    if (minutes < 0) {
      return null;
    }

    if (minutes === 0) {
      return new Timespan(0, 0);
    }

    if (minutes < 60) {
      return new Timespan(0, minutes);
    }

    const timespan = new Timespan(Math.floor(minutes / 60), minutes % 60);

    return timespan.isValid ? timespan : null;
  }

  static addMinutes(t: Timespan, minutes: number): Timespan {
    let h = t.hours;
    let m = t.minutes;

    m = m + minutes;

    if (m > 59) {
      h += Math.floor(m / 60);
      m = m % 60;
    }

    return new Timespan(h, m);
  }

  static parse(value: string | Date): Timespan | null {
    if (!value) {
      return null;
    }

    const stringValue = value instanceof Date ? DayDate.toTimeFormat(value) : value.toString();

    if (stringValue.length < 5) {
      return null;
    }

    const timeStr = stringValue.split(':');
    const timespan = new Timespan(+timeStr[0], timeStr[1] ? +timeStr[1] : 0);

    return timespan.isValid ? timespan : null;
  }

  static getDefaultStartTime(): Timespan {
    return new Timespan(9, 0);
  }

  static getDefaultEndTime(): Timespan {
    return new Timespan(17, 0);
  }

  format(): string {
    return this.formatToType('None');
  }

  formatToLocal(date: any = null, is24hAvailable: boolean = false): string {
    return this.formatToType('Local', date, is24hAvailable);
  }

  formatToUtc(date: any = null): string {
    return this.formatToType('Utc', date, false);
  }

  private formatToType(type: FormatType, date: Date = null, is24hAvailable: boolean = false): string {
    if (!this.isValid) {
      return '';
    }

    let hours = this.hours;
    let minutes = this.minutes;

    if (type !== 'None') {
      const offset = -(date ? DayDate.new(date, null, true) : DayDate.now()).utcOffset();

      const offsetHours = parseInt((offset / 60).toString());
      const offsetMins = offset % 60;

      const isFormatLocal = type === 'Local';

      const calculatedHours = isFormatLocal ? this.hours - offsetHours : this.hours + offsetHours;

      if (calculatedHours < 0) {
        // for example '00:00'-3h should be 24h - 3h = '21:00'
        hours = isFormatLocal ? 24 - offsetHours - hours : 24 + offsetHours + hours;
      } else if (calculatedHours === 24 && !is24hAvailable) {
        hours = 0;
      } else if (calculatedHours > 24) {
        hours = calculatedHours - 24;
      } else {
        hours = calculatedHours;
      }

      minutes = isFormatLocal ? this.minutes - offsetMins : this.minutes + offsetMins;
    }

    const formattedHour: string = hours < 10 ? `0${hours}` : hours.toString();
    const formattedMinute: string = minutes < 10 ? `0${minutes}` : minutes.toString();

    return `${formattedHour}:${formattedMinute}`;
  }
}
